import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { TextInput, PasswordInput, Stack, Checkbox } from '@mantine/core';

import logo from '@assets/images/tc-gymnasium-logo.png';
import { BasicButton } from '@components/Button';
import appStore from '@duck/app-store';
import { attachServerSessionToken } from '@util/axiosDefaults';
import { setSentryUserFromStore } from '@util/sentry';

import './index.scss';

export default function LoginForm({ isAdmin, isAthlete }) {
  const setAdmin = appStore((state) => state.setAdmin);
  const setAthlete = appStore((state) => state.setAthlete);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);

  const emailKey = `trainingTracker${isAdmin ? 'Admin' : 'Athlete'}Email`;
  const loginPath = isAdmin ? '/admin-login/' : '/athlete-login/';
  const buttonText = isAdmin ? 'Admin login' : 'Athlete login';
  const altPath = isAdmin ? '/athlete' : '/admin';
  const altLabel = isAdmin ? 'Athlete login' : 'Admin login';

  useEffect(() => {
    const storedEmail = localStorage.getItem(emailKey);
    if (storedEmail) {
      setEmail(storedEmail);
      setRemember(true);
    }
  }, [emailKey]);

  function applySession(session) {
    if (!session?.server_session_token) return;
    attachServerSessionToken(session.server_session_token);
    if (session.admin_id) {
      setAdmin(session);
    } else if (session.athlete_id) {
      setAthlete(session);
    }
    setSentryUserFromStore();
  }

  function onRememberChange(event) {
    const next = event.currentTarget.checked;
    setRemember(next);
    if (!next) {
      localStorage.removeItem(emailKey);
    } else if (email) {
      localStorage.setItem(emailKey, email);
    }
  }

  function onSubmit() {
    if (!email || !password) {
      return alert('please fill all the fields');
    }

    if (remember) {
      localStorage.setItem(emailKey, email);
    }

    setLoading(true);

    axios
      .post(loginPath, { email, password })
      .then(({ data }) => {
        applySession(data.response[0]);
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  const inputStyles = {
    label: { color: 'var(--brand-ink)', marginBottom: 6 },
    input: {
      background: 'var(--brand-surface-muted)',
      color: 'var(--brand-ink)',
      border: '1px solid var(--brand-sand)',
    },
    visibilityToggle: { color: 'var(--brand-ink-muted)' },
    description: { color: 'var(--brand-ink-muted)' },
  };

  return (
    <div className="login-page">
      <div className="login">
        <img src={logo} alt="T&C's Gymnasium" />
        <div className="login-form">
          <Stack gap="md">
            <TextInput
              label="Email"
              name="username"
              autoComplete="username"
              value={email}
              type="email"
              size="md"
              variant="filled"
              styles={inputStyles}
              onChange={(e) => setEmail(e.target.value)}
            />
            <PasswordInput
              label="Password"
              value={password}
              size="md"
              variant="filled"
              styles={inputStyles}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
            />
            <Checkbox
              label="Remember me"
              checked={remember}
              onChange={onRememberChange}
              styles={{ label: { color: 'var(--brand-ink)' } }}
              color="var(--brand-brick)"
            />
            <BasicButton
              text={buttonText}
              onClick={onSubmit}
              loading={loading}
              disabled={loading}
              color="var(--brand-brick)"
              variant="filled"
              size="md"
              style={{
                background: 'var(--brand-brick)',
                borderColor: 'var(--brand-brick)',
              }}
              primary
            />
            <Link
              className="login-secondary-link"
              to={
                isAdmin
                  ? '/admin-forgot-password'
                  : '/athlete-forgot-password'
              }
              style={{ textAlign: 'center', display: 'block' }}
            >
              Forgot password?
            </Link>

            <Link
              className="login-secondary-link"
              to={altPath}
              style={{ textAlign: 'center', display: 'block' }}
            >
              {altLabel}
            </Link>
          </Stack>
        </div>
      </div>
    </div>
  );
}

LoginForm.defaultProps = {
  isAdmin: false,
  isAthlete: false,
};
